import { Trans, useLingui } from '@lingui/react/macro'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import { FC, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'
import { RolodexEntryType, SensorEntryWrapper } from 'types'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const Row = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`

const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  font-size: 0.72rem;
  max-width: 100%;
`

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Remove = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
  line-height: 1;
`

const Suggestions = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 10rem;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

const Suggestion = styled.li`
  padding: 0.3rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.forecast.background2};
  }
`

const SuggestionTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SuggestionMeta = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Hint = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

interface TagOption {
  id: string
  /** Leading line: the tag's description when it has one. */
  title: string
  /** Dim second line: name/id, instrument tag and point source, deduped. */
  meta: string
}

export interface TagPickerProps {
  siteId: string
  label: ReactNode
  value: string[]
  onChange: (ids: string[]) => void
  multi?: boolean
  exclude?: string[]
}

export const TagPicker: FC<TagPickerProps> = ({
  siteId,
  label,
  value,
  onChange,
  multi = false,
  exclude = [],
}) => {
  const { t } = useLingui()

  const [query, setQuery] = useState('')
  const { data: wrappers } = useGetRolodexEntriesByLoadOptions<
    SensorEntryWrapper[]
  >({
    type: [RolodexEntryType.DATA_POINTS],
    tags: { site: [siteId] },
    limit: 10000,
  })

  const options = useMemo(() => {
    const excludeSet = new Set([...exclude, ...value])
    const q = query.trim().toLowerCase()
    const rows: TagOption[] = []
    for (const wrapper of wrappers ?? []) {
      const id = wrapper.entry.id
      if (excludeSet.has(id)) continue
      const data = wrapper.entry.data
      // Name, short id and instrument tag are usually the same plant code, so
      // the meta line dedupes rather than repeating it three times.
      const meta = [
        ...new Set(
          [
            data?.name,
            short(id),
            data?.instrument_tag,
            data?.point_source,
          ].filter(Boolean)
        ),
      ].join(' · ')
      // Descriptions are the readable part, so they lead the row and the
      // search — the codes alone tell you nothing about what a tag measures.
      const hay = `${id} ${data?.description ?? ''} ${meta}`.toLowerCase()
      if (q && !hay.includes(q)) continue
      rows.push({
        id,
        title: data?.description || data?.name || short(id),
        meta,
      })
      if (rows.length >= 40) break
    }
    return rows
  }, [wrappers, query, exclude, value])

  const pick = (id: string) => {
    if (multi) {
      onChange([...value, id])
    } else {
      onChange([id])
    }
    setQuery('')
  }

  const remove = (id: string) => {
    onChange(value.filter((v) => v !== id))
  }

  return (
    <Wrap>
      <Hint>{label}</Hint>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
        {value.map((id) => (
          <Chip key={id}>
            <TagLabel tagId={id}>
              <ChipText>{short(id)}</ChipText>
            </TagLabel>
            <Remove
              type='button'
              onClick={() => remove(id)}
              aria-label={t`Remove`}>
              ×
            </Remove>
          </Chip>
        ))}
      </div>
      {(multi || value.length === 0) && (
        <>
          <Row>
            <Input
              value={query}
              placeholder={t`Search tags…`}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Row>
          {query.trim() && (
            <Suggestions>
              {options.length === 0 ? (
                <Suggestion style={{ cursor: 'default' }}>
                  <Trans>No matches</Trans>
                </Suggestion>
              ) : (
                options.map((opt) => (
                  <Suggestion key={opt.id} onClick={() => pick(opt.id)}>
                    <TagLabel tagId={opt.id}>
                      <div style={{ minWidth: 0 }}>
                        <SuggestionTitle>{opt.title}</SuggestionTitle>
                        <SuggestionMeta>{opt.meta}</SuggestionMeta>
                      </div>
                    </TagLabel>
                  </Suggestion>
                ))
              )}
            </Suggestions>
          )}
        </>
      )}
    </Wrap>
  )
}
